import type { ManagedUser, RoleCode, UserCreate } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatPhone, formatStamp, normalizePhone } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';
import { useSite } from '../sites';

const ROLES: Exclude<RoleCode, 'customer'>[] = ['owner', 'office', 'warehouse', 'driver', 'sales', 'readonly'];

/** The owner's list of people: add one with a first password, change the role, reset a password, switch someone off. */
/** The same rule as the server: a letter first, then up to 29 letters, digits, dots, dashes or underscores; 2 to 30 long. */
const USERNAME_RE = /^[a-z][a-z0-9._-]{1,29}$/;

export function UsersPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const { sites } = useSite();
  const users = useQuery({ queryKey: qk.managedUsers, queryFn: ({ signal }) => api.managedUsers(signal) });
  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.managedUsers });
  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="stack">
      <PageHead title={t('usersTitle')} back="/" />
      <p className="muted">{t('usersHint')}</p>
      <QueryView query={users}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list" data-testid="users">
              {rows.map((u) => (
                <li key={u.id} className={u.active ? 'card stack-sm' : 'card stack-sm muted'} data-testid={`user-${u.id}`}>
                  <button type="button" className="row" onClick={() => setOpen(open === u.id ? null : u.id)} aria-expanded={open === u.id}>
                    <span className="row-main">
                      <span className="row-title">
                        {u.name} <span className="label">{t(`userRole_${u.role === 'customer' ? 'readonly' : u.role}`)}</span>
                        {!u.active ? <span className="badge badge-danger">{t('switchedOff')}</span> : null}
                        {!u.hasPassword ? <span className="badge badge-warn">{t('noPasswordYet')}</span> : null}
                      </span>
                      <span className="row-sub">
                        {[u.username, u.phone ? formatPhone(u.phone) : null, u.email].filter(Boolean).join(' · ')} · {t('lastSeen')} {u.lastSeenAt ? formatStamp(u.lastSeenAt, intlLocale) : t('never')}
                      </span>
                    </span>
                  </button>
                  {open === u.id ? <EditUser user={u} self={u.id === me?.id} sites={sites} onSaved={invalidate} /> : null}
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
      <AddUser sites={sites} onSaved={invalidate} />
    </div>
  );
}

function EditUser({ user: u, self, sites, onSaved }: { user: ManagedUser; self: boolean; sites: { id: string; name: string }[]; onSaved: () => void }) {
  const { t } = useLocale();
  const [role, setRole] = useState<Exclude<RoleCode, 'customer'>>(u.role === 'customer' ? 'readonly' : u.role);
  const [locale, setLocale] = useState(u.locale);
  const [homeSiteId, setHomeSiteId] = useState(u.homeSiteId ?? '');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState(u.username ?? '');
  const usernameBad = username.trim() !== '' && !USERNAME_RE.test(username.trim().toLowerCase());
  const patch = useMutation({ mutationFn: (body: Parameters<typeof api.patchUser>[1]) => api.patchUser(u.id, body), onSuccess: () => { setPassword(''); onSaved(); } });
  return (
    <div className="stack-sm" data-testid="edit-user">
      <div className="two-up">
        <label className="field">
          <span>{t('role')}</span>
          <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} disabled={self}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`userRole_${r}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('language')}</span>
          <select value={locale} onChange={(e) => setLocale(e.target.value as typeof locale)}>
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
        </label>
      </div>
      {sites.length > 1 ? (
        <label className="field">
          <span>{t('homeSite')}</span>
          <select value={homeSiteId} onChange={(e) => setHomeSiteId(e.target.value)}>
            <option value="">{t('anySite')}</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="field">
        <span>{t('username')}</span>
        <input autoComplete="off" autoCapitalize="none" spellCheck={false} aria-label={`${t('username')} ${u.name}`} value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t('usernameRule')} maxLength={30} />
      </label>
      {usernameBad ? <p className="field-error" role="alert">{t('usernameRule')}</p> : null}
      <label className="field">
        <span>{self ? t('newPassword') : t('resetPassword')}</span>
        <input type="password" autoComplete="new-password" aria-label={`${t('resetPassword')} ${u.name}`} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('passwordRule')} />
      </label>
      {patch.isError ? <ErrorBox error={patch.error} /> : null}
      {patch.isSuccess ? <p className="note" role="status">{t('saved')}</p> : null}
      <div className="two-up">
        <button
          type="button"
          className="btn btn-primary"
          disabled={patch.isPending || usernameBad || (password.length > 0 && password.length < 8)}
          onClick={() => patch.mutate({ role, locale, homeSiteId: homeSiteId || null, username: username.trim() ? username.trim().toLowerCase() : null, ...(password ? { password } : {}) })}
        >
          {t('save')}
        </button>
        {!self ? (
          <button type="button" className="btn btn-secondary" disabled={patch.isPending} onClick={() => { if (!u.active || window.confirm(`${t('switchOff')} ${u.name}: ${t('confirmSure')}`)) patch.mutate({ active: !u.active }); }}>
            {u.active ? t('switchOff') : t('switchOn')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function AddUser({ sites, onSaved }: { sites: { id: string; name: string }[]; onSaved: () => void }) {
  const { t } = useLocale();
  const [name, setName] = useState('');
  const [login, setLogin] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<UserCreate['role']>('office');
  const [locale, setLocale] = useState<UserCreate['locale']>('en');
  const [homeSiteId, setHomeSiteId] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const create = useMutation({
    mutationFn: (body: UserCreate) => api.createUser(body),
    onSuccess: () => {
      setName('');
      setLogin('');
      setUsername('');
      setPassword('');
      onSaved();
    },
  });
  const submit = () => {
    const phone = normalizePhone(login);
    const email = !phone && login.includes('@') ? login.trim().toLowerCase() : null;
    const user = username.trim().toLowerCase();
    if (user && !USERNAME_RE.test(user)) return setProblem('usernameRule');
    if (!name.trim() || (!phone && !email && !user)) return setProblem('loginNeeded');
    if (password.length < 8) return setProblem('passwordRule');
    setProblem(null);
    create.mutate({ name: name.trim(), ...(phone ? { phone } : {}), ...(email ? { email } : {}), ...(user ? { username: user } : {}), role, locale, ...(homeSiteId ? { homeSiteId } : {}), password });
  };
  return (
    <section className="card stack-sm" data-testid="add-user">
      <h2>{t('addPerson')}</h2>
      <label className="field">
        <span>{t('name')}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      </label>
      <label className="field">
        <span>{t('phoneOrEmail')}</span>
        <input inputMode="email" autoComplete="off" value={login} onChange={(e) => setLogin(e.target.value)} placeholder="(505) 555-1234" />
      </label>
      <label className="field">
        <span>{t('usernameOptional')}</span>
        <input autoComplete="off" autoCapitalize="none" spellCheck={false} value={username} onChange={(e) => setUsername(e.target.value)} placeholder={t('usernameRule')} maxLength={30} />
      </label>
      <div className="two-up">
        <label className="field">
          <span>{t('role')}</span>
          <select value={role} onChange={(e) => setRole(e.target.value as UserCreate['role'])}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`userRole_${r}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('language')}</span>
          <select value={locale} onChange={(e) => setLocale(e.target.value as UserCreate['locale'])}>
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
        </label>
      </div>
      {sites.length > 1 ? (
        <label className="field">
          <span>{t('homeSite')}</span>
          <select value={homeSiteId} onChange={(e) => setHomeSiteId(e.target.value)}>
            <option value="">{t('anySite')}</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="field">
        <span>{t('firstPassword')}</span>
        <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('passwordRule')} />
      </label>
      {problem ? <p className="field-error" role="alert">{t(problem)}</p> : null}
      {create.isError ? <ErrorBox error={create.error} /> : null}
      <button type="button" className="btn btn-primary btn-block" disabled={create.isPending} onClick={submit}>
        {t('addPerson')}
      </button>
    </section>
  );
}
